import '../styles/TransferHistoryLedger.css';
import React from 'react';
import { authHeader, request } from '../services/api';

const LEDGER_ENTRIES_ME_PATH = '/api/ledger_entries/me';

const SKELETON_ROWS = [0, 1, 2, 3];

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: '2-digit',
});

function formatDate(value) {
  if (!value) return 'Undated';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return DATE_FORMAT.format(parsed);
}

function formatAmount(value) {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '0.00';
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function sortByDateDesc(records) {
  return [...records].sort((a, b) => {
    const aTime = new Date(a && a.date ? a.date : 0).getTime() || 0;
    const bTime = new Date(b && b.date ? b.date : 0).getTime() || 0;
    return bTime - aTime;
  });
}

function LedgerRow({ entry }) {
  const isCredit = entry.direction === 'credit';
  const status = typeof entry.status === 'string' ? entry.status : '';
  const statusLabel = status ? status.replace(/[-_]/g, ' ') : 'recorded';
  const statusClass = status === 'settled' ? ' is-settled' : status === 'pending' ? ' is-pending' : '';

  return (
    <li className="thl-row">
      <div className="thl-row-readout">
        <span className="thl-row-date">{formatDate(entry.date)}</span>
        <span className="thl-row-desc">{entry.description}</span>
        <span className="thl-row-parties">
          Counterparty <span className="thl-party-name">{entry.counterparty}</span>
        </span>
      </div>

      <div className="thl-row-values">
        <span className={isCredit ? 'thl-amount is-credit' : 'thl-amount is-debit'}>
          {isCredit ? '+' : '−'}{formatAmount(entry.amount)}
        </span>
        <span className={'thl-status' + statusClass}>
          <span className="thl-status-dot" aria-hidden="true" />
          {statusLabel}
        </span>
      </div>
    </li>
  );
}

function useOwnTransferHistory() {
  const [state, setState] = React.useState({ data: [], loading: true, error: null });
  const [revision, setRevision] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    setState((value) => ({ ...value, loading: true, error: null }));
    request(LEDGER_ENTRIES_ME_PATH, { headers: authHeader() }).then(
      (data) => {
        if (active) setState({ data, loading: false, error: null });
      },
      (error) => {
        if (active) setState((value) => ({ ...value, loading: false, error: error.message || String(error) }));
      },
    );
    return () => {
      active = false;
    };
  }, [revision]);

  const refresh = React.useCallback(() => setRevision((value) => value + 1), []);
  return { ...state, refresh };
}

function TransferHistoryLedger() {
  const { data, loading, error, refresh } = useOwnTransferHistory();
  const [refreshing, setRefreshing] = React.useState(false);

  const records = Array.isArray(data) ? sortByDateDesc(data) : [];
  const busy = loading || refreshing;

  const handleRefresh = async () => {
    if (typeof refresh !== 'function') return;
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  const renderRows = () => {
    if (loading && records.length === 0) {
      return (
        <div className="thl-rows" role="status" aria-live="polite" aria-label="Loading transfer history">
          {SKELETON_ROWS.map((row) => (
            <div key={`skeleton-${row}`} className="thl-skeleton" aria-hidden="true">
              <span className="thl-bone is-short" />
              <span className="thl-bone is-mid" />
              <span className="thl-bone is-amount" />
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="thl-panel is-error" role="alert">
          <p className="thl-panel-mark">Readout interrupted</p>
          <h3 className="thl-panel-title">Transfer history unavailable</h3>
          <p className="thl-panel-body">
            The ledger could not be read. Your records are unchanged — retry when the connection settles.
          </p>
          <button type="button" className="thl-retry" onClick={handleRefresh}>
            Retry load
          </button>
        </div>
      );
    }

    if (records.length === 0) {
      return (
        <div className="thl-panel">
          <p className="thl-panel-mark">No readings on file</p>
          <h3 className="thl-panel-title">No transfers recorded</h3>
          <p className="thl-panel-body">
            No transfers have been made on this account yet. Completed transfers will appear here as
            ruled readouts, newest first.
          </p>
        </div>
      );
    }

    return (
      <ul className="thl-rows" aria-label="Past transfer records">
        {records.map((entry) => (
          <LedgerRow key={entry.id} entry={entry} />
        ))}
      </ul>
    );
  };

  const showColumns = !loading && !error && records.length > 0;

  return (
    <section className="thl-root" aria-labelledby="thl-ledger-title">
      <div className="thl-inner">
        <header className="thl-head">
          <div className="thl-head-titles">
            <p className="thl-micro">Transfer History</p>
            <h2 className="thl-title" id="thl-ledger-title">
              Recorded Transfer Ledger
            </h2>
            <p className="thl-meta">
              Every movement in and out of this account, measured as an instrument reading.
            </p>
          </div>

          <button
            type="button"
            className={busy ? 'thl-refresh is-spinning' : 'thl-refresh'}
            onClick={handleRefresh}
            disabled={busy}
          >
            <svg
              className="thl-refresh-icon"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="square"
              />
              <path d="M13.6 1.6v3.2h-3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" />
            </svg>
            {busy ? 'Refreshing' : 'Refresh'}
          </button>
        </header>

        {showColumns && (
          <div className="thl-columns" aria-hidden="true">
            <span>Date / Description</span>
            <span>Counterparty</span>
            <span className="thl-col-value thl-header-value-head">Amount / Status</span>
          </div>
        )}

        {renderRows()}

        <div className="thl-foot">
          <p className="thl-foot-note">
            {records.length} record{records.length === 1 ? '' : 's'} on file
          </p>
          <p className="thl-foot-note">Newest first</p>
        </div>
      </div>
    </section>
  );
}

export default TransferHistoryLedger;